import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { productsService } from '../services/productsService';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Save } from 'lucide-react';
import { Menu, ProductCategory, Product } from 'public-contracts';

const ModernMenuForm: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [productsByCategory, setProductsByCategory] = useState<
    Record<string, Product[]>
  >({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categorySelections: [] as Array<{
      categoryId: string;
      excludedProductIds: string[];
    }>,
    isActive: true,
  });

  useEffect(() => {
    loadCategories();
    loadAllProducts();
    if (isEdit && id) {
      loadMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadCategories = async () => {
    try {
      const categoriesData = await productsService.getProductCategories({
        page: 1,
        perPage: 1000,
      });
      const categoriesArray = Array.isArray(categoriesData)
        ? categoriesData
        : [];
      // Sort categories by level and name for hierarchical display
      const sorted = categoriesArray.sort((a, b) => {
        const levelA = a.level ?? 0;
        const levelB = b.level ?? 0;
        if (levelA !== levelB) return levelA - levelB;
        return (a.name || '').localeCompare(b.name || '');
      });
      setCategories(sorted);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadAllProducts = async () => {
    try {
      const products = await productsService.getAllProducts();
      // Group products by categoryId
      const grouped: Record<string, Product[]> = {};
      products.forEach((product) => {
        if (product.categoryId) {
          if (!grouped[product.categoryId]) {
            grouped[product.categoryId] = [];
          }
          grouped[product.categoryId].push(product);
        }
      });
      setProductsByCategory(grouped);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadMenu = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const menu = await productsService.getMenu(id);
      if (menu) {
        setFormData({
          name: menu.name || '',
          description: menu.description || '',
          categorySelections: menu.categorySelections || [],
          isActive: menu.isActive !== false,
        });
      } else {
        setError(translate('pos.menus.load_error'));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.menus.load_error')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (isEdit && id) {
        await productsService.updateMenu(id, formData);
      } else {
        await productsService.createMenu(formData);
      }
      navigate('/menus');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.menus.save_error')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryToggle = (categoryId: string, checked: boolean) => {
    const category = categories.find((c) => c.id === categoryId);
    
    if (checked) {
      // Add category selection
      // If this category has ancestors, we don't need to add them separately
      // because the backend will handle hierarchical queries
      setFormData((prev) => ({
        ...prev,
        categorySelections: [
          ...prev.categorySelections,
          { categoryId, excludedProductIds: [] },
        ],
      }));
    } else {
      // Remove category selection
      // Also remove any child categories that were implicitly included
      const categoryToRemove = categories.find((c) => c.id === categoryId);
      const childCategoryIds = categoryToRemove
        ? categories
            .filter(
              (c) =>
                c.ancestorIds?.includes(categoryId) ||
                c.parentId === categoryId
            )
            .map((c) => c.id)
        : [];

      setFormData((prev) => ({
        ...prev,
        categorySelections: prev.categorySelections.filter(
          (sel) =>
            sel.categoryId !== categoryId &&
            !childCategoryIds.includes(sel.categoryId)
        ),
      }));
    }
  };

  const handleProductToggle = (
    categoryId: string,
    productId: string,
    checked: boolean
  ) => {
    setFormData((prev) => {
      const categorySelection = prev.categorySelections.find(
        (sel) => sel.categoryId === categoryId
      );
      if (!categorySelection) return prev;

      const updatedSelections = prev.categorySelections.map((sel) => {
        if (sel.categoryId === categoryId) {
          if (checked) {
            // Remove from excluded (include in menu)
            return {
              ...sel,
              excludedProductIds: sel.excludedProductIds.filter(
                (id) => id !== productId
              ),
            };
          } else {
            // Add to excluded (exclude from menu)
            return {
              ...sel,
              excludedProductIds: [...sel.excludedProductIds, productId],
            };
          }
        }
        return sel;
      });

      return {
        ...prev,
        categorySelections: updatedSelections,
      };
    });
  };

  const isCategorySelected = (categoryId: string) => {
    return formData.categorySelections.some(
      (sel) => sel.categoryId === categoryId
    );
  };

  const isProductExcluded = (categoryId: string, productId: string) => {
    const categorySelection = formData.categorySelections.find(
      (sel) => sel.categoryId === categoryId
    );
    return categorySelection?.excludedProductIds.includes(productId) || false;
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          {translate('pos.menus.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto flex flex-col" style={{ height: 'calc(100vh - 3.5rem - 2rem)', maxHeight: 'calc(100vh - 3.5rem - 2rem)' }}>
      {/* Header */}
      <div className="flex items-center gap-4 flex-shrink-0 mb-6 px-6 pt-6">
        <Button
          variant="outline"
          onClick={() => navigate('/menus')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {translate('pos.common.action.back')}
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit
              ? translate('pos.common.action.edit')
              : translate('pos.menus.new_menu')}
          </h1>
          <p className="text-muted-foreground">
            {isEdit
              ? translate('pos.menus.edit_subtitle')
              : translate('pos.menus.create_subtitle')}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden px-6 pb-6">
        <div className="flex-1 flex flex-col min-h-0 gap-6 overflow-hidden">
          {/* Basic Info */}
          <Card className="flex-shrink-0">
            <CardHeader>
            <CardTitle>
              {translate('pos.menus.menu_details')}
            </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">
                  {translate('pos.menus.fields.name')} *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                  placeholder={translate(
                    'pos.menus.name_placeholder'
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {translate('pos.menus.fields.description')}
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder={translate(
                    'pos.menus.description_placeholder'
                  )}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Category Selection */}
          <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <CardHeader className="flex-shrink-0 pb-4">
              <CardTitle>
                {translate('pos.menus.select_categories')}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 overflow-y-auto pr-2">
              <div className="space-y-4">
                {categories.map((category) => {
                  const isSelected = isCategorySelected(category.id);
                  const products = productsByCategory[category.id] || [];
                  const level = category.level ?? 0;
                  const indentClass = level > 0 ? `ml-${level * 6}` : '';
                  const parentCategory = category.parentId
                    ? categories.find((c) => c.id === category.parentId)
                    : null;

                  return (
                    <div
                      key={category.id}
                      className={`border rounded-lg p-4 space-y-4 ${indentClass}`}
                    >
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`category-${category.id}`}
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handleCategoryToggle(category.id, checked === true)
                          }
                        />
                        <Label
                          htmlFor={`category-${category.id}`}
                          className="text-lg font-semibold cursor-pointer"
                        >
                          {category.name}
                          {parentCategory && (
                            <span className="text-sm text-muted-foreground ml-2 font-normal">
                              ({translate('pos.menus.parent_category')}: {parentCategory.name})
                            </span>
                          )}
                          {level > 0 && (
                            <span className="text-xs text-muted-foreground ml-2 font-normal">
                              (Level {level})
                            </span>
                          )}
                        </Label>
                        {products.length > 0 && (
                          <span className="text-sm text-muted-foreground">
                            ({products.length} {translate('pos.menus.products')})
                          </span>
                        )}
                      </div>

                      {isSelected && products.length > 0 && (
                          <div className="ml-6 space-y-2 border-l-2 pl-4">
                            <p className="text-sm text-muted-foreground mb-2">
                              {translate('pos.menus.exclude_products')}
                            </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {products.map((product) => {
                              const isExcluded = isProductExcluded(
                                category.id,
                                product.id
                              );
                              return (
                                <div
                                  key={product.id}
                                  className="flex items-center space-x-2"
                                >
                                  <Checkbox
                                    id={`product-${product.id}`}
                                    checked={!isExcluded}
                                    onCheckedChange={(checked) =>
                                      handleProductToggle(
                                        category.id,
                                        product.id,
                                        checked === true
                                      )
                                    }
                                  />
                                  <Label
                                    htmlFor={`product-${product.id}`}
                                    className="text-sm cursor-pointer flex-1"
                                  >
                                    {(product as any).name || product.reference || product.id}
                                    {product.reference && (product as any).name && (
                                      <span className="text-muted-foreground ml-2 text-xs">
                                        ({product.reference})
                                      </span>
                                    )}
                                    {product.price && (
                                      <span className="text-muted-foreground ml-2">
                                        - {product.price.toLocaleString()} VND
                                      </span>
                                    )}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {categories.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {translate('pos.menus.no_categories')}
                </p>
              )}
            </CardContent>
          </Card>


          {/* Actions */}
          <div className="flex gap-4 flex-shrink-0 pt-2">
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving
                ? translate('common.saving')
                : isEdit
                ? translate('pos.common.action.save')
                : translate('pos.common.action.create')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/menus')}
            >
              {translate('pos.common.action.cancel')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ModernMenuForm;

